import type { Invoice } from '@/entities/invoices/invoice.entity';
import { Alert } from '@/services/alert.service';
import { useRegenerateInvoicePdf, useSendInvoiceToCustomer } from '@/queries/use-invoices';
import { InvoiceStatus } from '@/entities/invoices/invoice-status.enum';
import { useIsDeveloper } from '@/hooks/use-is-developer';
import { useI18n } from 'vue-i18n';
import { computed } from 'vue';
import { usePdfPreview } from '@/hooks/use-pdf-preview';
import { downloadBlob, fetchBlobFromUrl, fetchPdfBlobFromUrl } from '@/utils/download-file';

export const useInvoicePdfs = () => {
  const { t } = useI18n({
    useScope: 'global',
    legacy: false,
    fallbackLocale: 'en',
    globalInjection: true,
    fallbackWarn: false,
    missingWarn: false,
    messages: localization,
  });

  const { mutateAsync: sendInvoiceToCustomer, isPending: isSending } = useSendInvoiceToCustomer();
  const { mutateAsync: regenerateInvoicePdf, isPending: isRegeneratingPdf } =
    useRegenerateInvoicePdf();
  const pdfPreview = usePdfPreview();
  const isDeveloper = useIsDeveloper();

  /** Developers can re-queue PDF generation for opened invoices that have no PDF. */
  const canRegenerateInvoicePdf = (invoice: Invoice) =>
    isDeveloper.value &&
    !invoice.fileUrl &&
    invoice.status !== InvoiceStatus.DRAFT &&
    invoice.status !== InvoiceStatus.PRELIMINARY;
  const regeneratePdfLabel = computed(() => t('regeneratePdf'));

  const downloadInvoice = async (invoice: Invoice) => {
    if (!invoice.fileUrl) return;
    await pdfPreview.openPdfPreview({
      title: t('invoicePdfTitle', {
        number: invoice.completeInvoiceNumber ?? invoice.id,
      }),
      fileName: `${invoice.completeInvoiceNumber ?? `invoice-${invoice.id}`}.pdf`,
      load: () => fetchPdfBlobFromUrl(invoice.fileUrl!),
      errorMessage: t('couldNotLoadPdf'),
    });
  };

  const downloadInvoiceDirect = async (invoice: Invoice) => {
    if (!invoice.fileUrl) return;
    const blob = await fetchBlobFromUrl(invoice.fileUrl);
    await downloadBlob(blob, `${invoice.completeInvoiceNumber ?? `invoice-${invoice.id}`}.pdf`);
  };

  const sendInvoiceToCustomerWithAlert = async (invoiceId: string) => {
    const alertResult = await Alert.fire({
      titleText: t('reallySendInvoiceTitle'),
      text: t('reallySendInvoiceText'),
      icon: 'question',
      showDenyButton: true,
      confirmButtonText: t('sendInvoice'),
      denyButtonText: t('cancel'),
    });
    if (alertResult.isConfirmed) {
      sendInvoiceToCustomer(invoiceId);
    }
  };

  return {
    downloadInvoice,
    downloadInvoiceDirect,
    sendInvoiceToCustomer,
    sendInvoiceToCustomerWithAlert,
    isSending,
    canRegenerateInvoicePdf,
    regenerateInvoicePdf,
    isRegeneratingPdf,
    regeneratePdfLabel,
  };
};

const localization = {
  en: {
    reallySendInvoiceTitle: 'Really send Invoice?',
    reallySendInvoiceText: 'Are you sure, you want to send this Invoice to the Customer?',
    sendInvoice: 'Send Invoice',
    invoicePdfTitle: 'Invoice {number}',
    couldNotLoadPdf: 'The PDF could not be loaded. Please try again.',
    regeneratePdf: 'Generate PDF (developer)',
  },
  de: {
    reallySendInvoiceTitle: 'Rechnung wirklich versenden?',
    reallySendInvoiceText:
      'Bist du sicher, dass du diese Rechnung an den Kunden versenden möchtest?',
    sendInvoice: 'Rechnung verschicken',
    invoicePdfTitle: 'Rechnung {number}',
    couldNotLoadPdf: 'Das PDF konnte nicht geladen werden. Bitte versuche es erneut.',
    regeneratePdf: 'PDF erzeugen (Entwickler)',
  },
};
